// Events publishing + flyer preview (posting restricted to members/admins)
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../auth/AuthProvider";
import { useMemberProfile } from "../../hooks/useMemberProfile";
import { resolveEventMediaUrl, isVideoUrl } from "../../lib/eventMedia";

// media resolving handled via src/lib/eventMedia

export default function EventsSection({ api }) {
  const {
    EVENT_POSTS_KEY,
    NOTIFY_EMAIL,
    eventDraft,
    eventFlyerIndex,
    eventFlyerPreviews,
    eventStatus,
    events,
    saveToStorage,
    sendEmail,
    setEventDraft,
    setEventFlyerIndex,
    setEventFlyerPreviews,
    setEventStatus,
    setEvents,
    t,
  } = api || {};

  const { user, isAdmin } = useAuth();
  const { planKey } = useMemberProfile();

  const localMember =
    typeof window !== "undefined" &&
    window.localStorage.getItem("wow5050_member") === "1";
  const isMember = !!user || localMember || String(planKey || "free").toLowerCase() !== "free";
  const canPost = !!user && (isAdmin || isMember);

  const draft = eventDraft || {
    title: "",
    date: "",
    location: "",
    description: "",
    link: "",
    flyers: [],
    flyerFiles: [],
  };

  const previews = Array.isArray(eventFlyerPreviews) ? eventFlyerPreviews : [];
  const flyerIndex = Number.isFinite(eventFlyerIndex) ? eventFlyerIndex : 0;
  const [remoteEvents, setRemoteEvents] = useState([]);
  const [mediaMap, setMediaMap] = useState({});
  // Remote errors are logged to console; UI stays clean.

useEffect(() => {
  let alive = true;

  (async () => {
    try {
      const nowIso = new Date().toISOString();

      const { data, error } = await supabase
        .from("wow5050_events")
        .select("id,title,description,location,date,link,flyers,created_at")
        .order("date", { ascending: true });

      if (!alive) return;

      if (error) {
        // Hide schema-cache/table-missing noise from the UI (still log)
        const msg = String(error?.message || "");
        const code = String(error?.code || "");
        if (code === "PGRST205" || msg.includes("schema cache") || msg.includes("Could not find the table")) {
          console.warn("Events backend not ready:", msg);
          setRemoteEvents([]);
          return;
        }
        console.warn("Homepage events fetch failed:", error);
        setRemoteEvents([]);
        return;
      }

      const mapped = (data || []).map((r) => ({
        id: r.id,
        title: r.title || "",
        description: r.description || "",
        location: r.location || "",
        date: r.date,
        link: r.link || "",
        flyers: Array.isArray(r.flyers) ? r.flyers : [],
        createdAt: r.created_at ? new Date(r.created_at).getTime() : Date.now(),
      }));

      setRemoteEvents(mapped);
    } catch (e) {
      console.warn("Homepage events fetch crash:", e);
      if (alive) {
        setRemoteEvents([]);
      }
    }
  })();

  return () => {
    alive = false;
  };
}, []);

const localList = Array.isArray(events) ? events : [];

const list = useMemo(() => {
  const byId = new Map();
  for (const ev of [...remoteEvents, ...localList]) {
    if (!ev) continue;
    const id = ev.id || ev.event_id || ev.uuid;
    if (!id) continue;
    byId.set(id, { ...ev, id });
  }

  return Array.from(byId.values()).sort((a, b) => {
    const da = a?.date ? new Date(a.date).getTime() : 0;
    const db = b?.date ? new Date(b.date).getTime() : 0;
    // Upcoming first (soonest), then past
    const now = Date.now();
    const aUpcoming = da >= now;
    const bUpcoming = db >= now;
    if (aUpcoming && !bUpcoming) return -1;
    if (!aUpcoming && bUpcoming) return 1;
    if (aUpcoming && bUpcoming) return da - db;
    return db - da;
  });
}, [remoteEvents, localList]);

  // Resolve first flyer/video URL for each event (supports legacy filename rows)
  useEffect(() => {
    let alive = true;
    (async () => {
      const next = {};
      for (const ev of list) {
        const first = (Array.isArray(ev?.flyers) && ev.flyers[0]) || "";
        if (!first) continue;
        try {
          const url = await resolveEventMediaUrl(first, ev.id);
          if (url) next[ev.id] = url;
        } catch {
          // ignore
        }
      }
      if (alive) setMediaMap(next);
    })();
    return () => {
      alive = false;
    };
  }, [
    list.map((e) => `${e.id}:${Array.isArray(e.flyers) ? e.flyers.join(",") : ""}`).join("|"),
  ]);


  const clearPreviews = () => {
    setEventFlyerPreviews?.((prev) => {
      (prev || []).forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch {}
      });
      return [];
    });
    setEventFlyerIndex?.(0);
  };

  const clearDraft = () => {
    setEventDraft?.({
      title: "",
      date: "",
      location: "",
      description: "",
      link: "",
      flyers: [],
    });
    clearPreviews();
  };

  const publishEvent = async () => {
    setEventStatus?.("");

    if (!draft.title.trim() || !draft.description.trim()) {
      alert("Please add an event title and description.");
      return;
    }

    // 1) Upload media (images/videos) to Supabase Storage (event-flyers bucket)
    const files = Array.isArray(draft.flyerFiles) ? draft.flyerFiles : [];
    let uploadedUrls = [];
    let newId = null;

    try {
      // Insert first to get a real UUID
      const eventDateIso = draft.date
        ? new Date(`${draft.date}T12:00:00`).toISOString()
        : null;

      const { data: inserted, error: insErr } = await supabase
        .from("wow5050_events")
        .insert([
          {
            title: draft.title,
            description: draft.description,
            location: draft.location,
            date: eventDateIso,
            link: draft.link,
            flyers: [],
            createdBy: user?.id || null,
          },
        ])
        .select("id")
        .single();

      if (insErr) throw insErr;
      newId = inserted?.id;

      if (newId && files.length) {
        const bucket = supabase.storage.from("event-flyers");

        for (const f of files) {
          const safeName = String(f.name || "media").replace(/[^a-zA-Z0-9._-]+/g, "_");
          const path = `events/${newId}/${Date.now()}_${safeName}`;

          const { error: upErr } = await bucket.upload(path, f, {
            cacheControl: "3600",
            upsert: true,
            contentType: f.type || undefined,
          });
          if (upErr) throw upErr;

          const { data: pub } = bucket.getPublicUrl(path);
          if (pub?.publicUrl) uploadedUrls.push(pub.publicUrl);
        }

        // Update row with URLs
        await supabase
          .from("wow5050_events")
          .update({ flyers: uploadedUrls })
          .eq("id", newId);
      }

      // Refresh list from Supabase
      const { data: refreshed } = await supabase
        .from("wow5050_events")
        .select("id,title,description,location,date,link,flyers,created_at")
        .order("date", { ascending: true });

      const mapped = (refreshed || []).map((r) => ({
        id: r.id,
        title: r.title || "",
        description: r.description || "",
        location: r.location || "",
        date: r.date,
        link: r.link || "",
        flyers: Array.isArray(r.flyers) ? r.flyers : [],
        createdAt: r.created_at ? new Date(r.created_at).getTime() : Date.now(),
      }));
      setRemoteEvents(mapped);

      // 2) Notify via email (best-effort; never blocks)
      try {
        await fetch("/api/send-email", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            type: "event",
            email: user?.email || "",
            title: draft.title,
            date: eventDateIso,
            location: draft.location,
            link: draft.link,
            description: draft.description,
            flyers: uploadedUrls,
          }),
        });
      } catch (e) {
        console.warn("Event email notify failed (non-blocking)", e);
      }

      setEventStatus?.("Published.");
      alert("Event published.");
      clearDraft();
    } catch (e) {
      console.warn("Publish event failed:", e);
      alert(
        `Publish failed.\n\nMost common causes:\n- Supabase table/policies not set\n- Storage bucket/policies not set\n\nError: ${e?.message || e}`
      );
    }
  };

  return (
    <section id="events" className="scroll-mt-24">
      <div className="mx-auto max-w-6xl px-4">
        <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-emerald-300">
          {t?.eventsTitle || "Events"}
        </h2>
        <p className="mt-2 text-slate-200/90">{t?.eventsBody || ""}</p>

        {!canPost ? (
          <div className="wow-box mt-6 p-6">
            <div className="rounded-2xl border border-slate-800 bg-[#010916]/70 p-5">
              <div className="text-lg font-semibold text-emerald-300">
                Members &amp; Admins only
              </div>
              <p className="mt-1 text-slate-200/90">
                To publish events and upload flyers, please log in with a confirmed membership (or an admin account).
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  className="rounded-2xl border border-cyan-500/60 bg-cyan-500/10 px-4 py-2 text-sm text-cyan-200"
                  onClick={() => {
                    window.location.href = "/login";
                  }}
                >
                  Log In
                </button>
                <button
                  type="button"
                  className="rounded-2xl border border-emerald-500/60 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-200"
                  onClick={() => {
                    window.location.href = "/join";
                  }}
                >
                  Join
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="wow-box mt-6 p-6">
            <div className="flex flex-col gap-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="text-xs text-slate-300">Event Title</label>
                  <input
                    className="mt-1 w-full rounded-2xl border border-slate-700 bg-slate-950/60 px-4 py-3 text-slate-100 outline-none focus:border-cyan-400"
                    value={draft.title}
                    onChange={(e) => setEventDraft?.((p) => ({ ...(p || {}), title: e.target.value }))}
                    placeholder="Community event / fundraiser / activation"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-300">Date</label>
                  <input
                    type="date"
                    className="mt-1 w-full rounded-2xl border border-slate-700 bg-slate-950/60 px-4 py-3 text-slate-100 outline-none focus:border-cyan-400"
                    value={draft.date}
                    onChange={(e) => setEventDraft?.((p) => ({ ...(p || {}), date: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="text-xs text-slate-300">Location</label>
                  <input
                    className="mt-1 w-full rounded-2xl border border-slate-700 bg-slate-950/60 px-4 py-3 text-slate-100 outline-none focus:border-cyan-400"
                    value={draft.location}
                    onChange={(e) =>
                      setEventDraft?.((p) => ({ ...(p || {}), location: e.target.value }))
                    }
                    placeholder="City / Venue / Online"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-300">Link (optional)</label>
                  <input
                    className="mt-1 w-full rounded-2xl border border-slate-700 bg-slate-950/60 px-4 py-3 text-slate-100 outline-none focus:border-cyan-400"
                    value={draft.link}
                    onChange={(e) => setEventDraft?.((p) => ({ ...(p || {}), link: e.target.value }))}
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-300">Description</label>
                <textarea
                  className="mt-1 min-h-[120px] w-full rounded-2xl border border-slate-700 bg-slate-950/60 px-4 py-3 text-slate-100 outline-none focus:border-cyan-400"
                  value={draft.description}
                  onChange={(e) =>
                    setEventDraft?.((p) => ({ ...(p || {}), description: e.target.value }))
                  }
                  placeholder="Tell people what the event is about, who it's for, and how to participate."
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-slate-700 bg-slate-950/40 p-4">
                  <div className="text-sm font-semibold text-slate-100">Event Flyer Upload</div>
                  <div className="mt-2 text-xs text-slate-300">
                    Add one or multiple flyers. We’ll show a slider preview.
                  </div>
                  <input
                    className="mt-3 block w-full text-sm text-slate-200 file:mr-4 file:rounded-full file:border file:border-emerald-400/60 file:bg-emerald-500/10 file:px-4 file:py-2 file:text-emerald-200 hover:file:bg-emerald-500/15"
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      const names = files.map((f) => f.name);
                      setEventDraft?.((p) => ({ ...(p || {}), flyers: names, flyerFiles: files }));

                      const urls = files.map((f) => URL.createObjectURL(f));
                      setEventFlyerPreviews?.((prev) => {
                        (prev || []).forEach((u) => {
                          try {
                            URL.revokeObjectURL(u);
                          } catch {}
                        });
                        return urls;
                      });
                      setEventFlyerIndex?.(0);
                    }}
                  />
                </div>

                <div className="rounded-2xl border border-slate-700 bg-slate-950/40 p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-100">Flyer Preview</div>
                    <div className="text-xs text-slate-300">
                      {previews.length
                        ? `${Math.min(flyerIndex + 1, previews.length)}/${previews.length}`
                        : "No flyer selected"}
                    </div>
                  </div>

                  <div className="mt-3 aspect-[16/10] w-full overflow-hidden rounded-2xl border border-slate-700 bg-black/20">
                    {previews.length ? (
                      (() => {
                        const src = previews[Math.max(0, Math.min(flyerIndex, previews.length - 1))];
                        const f = (draft.flyerFiles || [])[Math.max(0, Math.min(flyerIndex, previews.length - 1))];
                        const isVid = !!f && String(f.type || "").startsWith("video/");
                        return isVid ? (
                          <video src={src} controls className="h-full w-full object-contain" />
                        ) : (
                          <img src={src} alt="Flyer preview" className="h-full w-full object-contain" />
                        );
                      })()
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-sm text-slate-400">
                        Upload flyer images to preview here.
                      </div>
                    )}
                  </div>

                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      className="rounded-full border border-slate-600 bg-slate-950/50 px-4 py-2 text-sm text-slate-200 hover:bg-slate-900/60 disabled:opacity-40"
                      disabled={!previews.length}
                      onClick={() =>
                        setEventFlyerIndex?.((i) =>
                          i <= 0 ? Math.max(0, previews.length - 1) : i - 1
                        )
                      }
                    >
                      Prev
                    </button>
                    <button
                      type="button"
                      className="rounded-full border border-slate-600 bg-slate-950/50 px-4 py-2 text-sm text-slate-200 hover:bg-slate-900/60 disabled:opacity-40"
                      disabled={!previews.length}
                      onClick={() =>
                        setEventFlyerIndex?.((i) => (previews.length ? (i + 1) % previews.length : 0))
                      }
                    >
                      Next
                    </button>
                    <button
                      type="button"
                      className="ml-auto rounded-full border border-emerald-400/60 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-200 hover:bg-emerald-500/15"
                      onClick={clearDraft}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  className="rounded-full border border-emerald-400/60 bg-emerald-500/10 px-6 py-3 font-semibold text-emerald-200 hover:bg-emerald-500/15"
                  onClick={publishEvent}
                >
                  Publish Event
                </button>

                <div className="text-sm text-slate-300">
                  {eventStatus ||
                    "Publishing saves to this device and sends a notification to WOW inbox."}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="wow-box mt-6 p-6">
          <div className="text-lg font-semibold text-slate-100">
            Current Events ({list.length})
          </div>
          <div className="mt-3 space-y-3">
            {list.length ? (
              list.map((ev) => (
                <div
                  key={ev.id}
                  className="rounded-2xl border border-slate-700 bg-slate-950/40 p-4"
                >
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="font-semibold text-slate-100">{ev.title}</div>
                      <div className="text-xs text-slate-300">
                        {(ev.date
                          ? new Date(ev.date).toLocaleDateString()
                          : "Date TBD")} • {ev.location || "Location TBD"}
                      </div>
                    </div>
                    {ev.link ? (
                      <a
                        className="mt-2 text-sm text-cyan-300 hover:underline sm:mt-0"
                        href={ev.link}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open Link
                      </a>
                    ) : null}
                  </div>

                  {/* Flyer / video thumbnail */}
                  {(() => {
                    const url = mediaMap?.[ev.id] || "";
                    if (!url) return null;
                    const vid = isVideoUrl(url);
                    return (
                      <button
                        type="button"
                        onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                        className="mt-3 block w-full overflow-hidden rounded-2xl border border-slate-700 bg-black/30"
                        style={{ cursor: "zoom-in" }}
                        aria-label="Open media"
                      >
                        {vid ? (
                          <video src={url} controls className="h-[260px] w-full object-contain" />
                        ) : (
                          <img
                            src={url}
                            alt="Event flyer"
                            className="h-[260px] w-full object-contain"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        )}
                      </button>
                    );
                  })()}

                  <div className="mt-2 whitespace-pre-wrap text-sm text-slate-200/90">
                    {ev.description}
                  </div>
                  {Array.isArray(ev.flyers) && ev.flyers.length ? (
                    <div className="mt-2 text-xs text-slate-300">
                      Media: {ev.flyers.length} file{ev.flyers.length === 1 ? "" : "s"}
                    </div>
                  ) : null}
                </div>
              ))
            ) : (
              <div className="text-sm text-slate-300">No events published yet.</div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
